import type { Labyrinth } from './Labyrinth';

/**
 * A* pathfinder with binary heap for performance on large maps.
 * Returns an array of [x, y] tile positions from start to goal (inclusive),
 * or null if no path exists.
 */
export function findPath(
  labyrinth: Labyrinth,
  sx: number,
  sy: number,
  gx: number,
  gy: number,
  maxIterations = 80000,
): [number, number][] | null {
  if (!labyrinth.isWalkable(gx, gy)) return null;
  if (sx === gx && sy === gy) return [[sx, sy]];

  const w = labyrinth.width;
  const key = (x: number, y: number) => y * w + x;

  const gScore = new Map<number, number>();
  const cameFrom = new Map<number, number>();
  const closed = new Set<number>();

  const sk = key(sx, sy);
  const gk = key(gx, gy);
  gScore.set(sk, 0);

  // Binary min-heap on fScore
  const heap = new MinHeap();
  heap.push(sk, heuristic(sx, sy, gx, gy));

  const dirs: [number, number][] = [[1, 0], [-1, 0], [0, 1], [0, -1]];

  let iterations = 0;
  while (heap.size > 0 && iterations < maxIterations) {
    iterations++;

    const current = heap.pop()!;

    if (current === gk) {
      return reconstructPath(cameFrom, current, w);
    }

    if (closed.has(current)) continue;
    closed.add(current);

    const cx = current % w;
    const cy = (current - cx) / w;
    const cg = gScore.get(current)!;

    for (const [dx, dy] of dirs) {
      const nx = cx + dx;
      const ny = cy + dy;
      if (!labyrinth.isWalkable(nx, ny)) continue;

      const nk = key(nx, ny);
      if (closed.has(nk)) continue;

      const tentG = cg + 1;
      const prevG = gScore.get(nk);

      if (prevG === undefined || tentG < prevG) {
        cameFrom.set(nk, current);
        gScore.set(nk, tentG);
        heap.push(nk, tentG + heuristic(nx, ny, gx, gy));
      }
    }
  }

  return null;
}

function heuristic(ax: number, ay: number, bx: number, by: number): number {
  return Math.abs(ax - bx) + Math.abs(ay - by);
}

function reconstructPath(
  cameFrom: Map<number, number>,
  current: number,
  w: number,
): [number, number][] {
  const path: [number, number][] = [];
  let c = current;
  while (c !== undefined) {
    const x = c % w;
    const y = (c - x) / w;
    path.push([x, y]);
    const prev = cameFrom.get(c);
    if (prev === undefined) break;
    c = prev;
  }
  path.reverse();
  return path;
}

// -------------------------------------------------------
// Binary min-heap keyed by priority (fScore)
// -------------------------------------------------------
class MinHeap {
  private data: number[] = [];
  private priorities: number[] = [];

  get size() { return this.data.length; }

  push(value: number, priority: number) {
    this.data.push(value);
    this.priorities.push(priority);
    this.bubbleUp(this.data.length - 1);
  }

  pop(): number | undefined {
    if (this.data.length === 0) return undefined;
    const top = this.data[0];
    const last = this.data.length - 1;
    if (last > 0) {
      this.data[0] = this.data[last]!;
      this.priorities[0] = this.priorities[last]!;
    }
    this.data.pop();
    this.priorities.pop();
    if (this.data.length > 0) this.sinkDown(0);
    return top;
  }

  private bubbleUp(i: number) {
    while (i > 0) {
      const parent = (i - 1) >> 1;
      if (this.priorities[i]! < this.priorities[parent]!) {
        this.swap(i, parent);
        i = parent;
      } else break;
    }
  }

  private sinkDown(i: number) {
    const n = this.data.length;
    while (true) {
      let smallest = i;
      const l = 2 * i + 1;
      const r = 2 * i + 2;
      if (l < n && this.priorities[l]! < this.priorities[smallest]!) smallest = l;
      if (r < n && this.priorities[r]! < this.priorities[smallest]!) smallest = r;
      if (smallest !== i) {
        this.swap(i, smallest);
        i = smallest;
      } else break;
    }
  }

  private swap(a: number, b: number) {
    [this.data[a], this.data[b]] = [this.data[b]!, this.data[a]!];
    [this.priorities[a], this.priorities[b]] = [this.priorities[b]!, this.priorities[a]!];
  }
}
